import React, { useState, useEffect, useMemo, useRef } from "react";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue, push, set, remove, update } from "firebase/database";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import FlowCreatorPage from "./FlowCreatorPage";
import "./LeadSettings.css";

// Vordefinierte Touchpoints (gleich wie in Leads.js)
const TOUCHPOINT_OPTIONS = {
  // Meetings & Calls (High Value)
  meet_in_person: { label: "Meet In Person", icon: "fa-users", points: 4 },
  sales_meeting: { label: "Sales Meeting", icon: "fa-handshake", points: 4 },
  online_meeting: { label: "Online Meeting", icon: "fa-video", points: 3 },
  phone_call: { label: "Phone Call", icon: "fa-phone-alt", points: 2 },
  
  // Sales Process
  demo_given: { label: "Demo Given", icon: "fa-play-circle", points: 3 },
  proposal_sent: { label: "Proposal Sent", icon: "fa-file-alt", points: 3 },
  quote_sent: { label: "Quote Sent", icon: "fa-file-invoice-dollar", points: 2 },
  contract_signed: { label: "Contract Signed", icon: "fa-file-signature", points: 5 },
  
  // Direct Communication
  email_sent: { label: "Send Email", icon: "fa-paper-plane", points: 1 },
  loom_video: { label: "Loom Video Sent", icon: "fa-video", points: 2 },
  whatsapp_message: { label: "WhatsApp Message", icon: "fa-whatsapp", isBrand: true, points: 1 },
  linkedin_message: { label: "LinkedIn Message", icon: "fa-linkedin", isBrand: true, points: 1 },
  instagram_dm: { label: "Instagram DM", icon: "fa-instagram", isBrand: true, points: 1 },
  follow_up: { label: "Follow Up", icon: "fa-redo", points: 1 },
  
  // Content & Events
  webinar: { label: "Webinar Invite", icon: "fa-desktop", points: 2 },
  lead_magnet: { label: "Send Lead Magnet", icon: "fa-magnet", points: 1 },
  
  // Social
  linkedin_connect: { label: "LinkedIn Connect", icon: "fa-linkedin", isBrand: true, points: 0.5 },
  instagram_follow: { label: "Instagram Follow", icon: "fa-instagram", isBrand: true, points: 0.5 },
};

// Start Trigger Optionen für Retargeting Flows
const TRIGGER_OPTIONS_RETARGETING = {
  lead_created: { label: "When Lead is Created", icon: "fa-plus-circle" },
  manual: { label: "Manual Start", icon: "fa-hand-pointer" },
  touchpoint_reached: { label: "After Specific Touchpoint", icon: "fa-check-circle" },
  score_reached: { label: "When Score Reaches", icon: "fa-chart-line" },
};

// Start Trigger Optionen für Warmup Flows (nur manual für kalte Kontakte)
const TRIGGER_OPTIONS_WARMUP = {
  manual: { label: "Manual Start", icon: "fa-hand-pointer" },
};

const LeadSettings = ({ userId }) => {
  // Both flow types now live on one screen. Each flow keeps its type in its
  // own record; the type also decides which RTDB path it is stored under.
  const [warmupFlows, setWarmupFlows] = useState([]);
  const [retargetingFlows, setRetargetingFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFlow, setEditingFlow] = useState(null);
  const [customTouchpoints, setCustomTouchpoints] = useState([]);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [showTouchpointsMenu, setShowTouchpointsMenu] = useState(false);
  const touchpointsMenuRef = useRef(null);
  const [newCustomTouchpoint, setNewCustomTouchpoint] = useState({ label: '', icon: 'fa-circle', points: 1 });
  const [showVisualBuilder, setShowVisualBuilder] = useState(false);
  const [visualFlowData, setVisualFlowData] = useState({ nodes: [], edges: [] });
  const [visualFlowName, setVisualFlowName] = useState('');
  const [visualFlowType, setVisualFlowType] = useState('warmup');
  const [visualFlowTrigger, setVisualFlowTrigger] = useState('manual');
  const [visualFlowTriggerValue, setVisualFlowTriggerValue] = useState('');
  const [emailTopics, setEmailTopics] = useState([]);
  const [senderEmails, setSenderEmails] = useState([]);

  // Combined list of all flows, each tagged with its type from its source path.
  const flows = useMemo(() => [
    ...warmupFlows.map((f) => ({ ...f, flowType: 'warmup' })),
    ...retargetingFlows.map((f) => ({ ...f, flowType: 'retargeting' })),
  ], [warmupFlows, retargetingFlows]);

  // RTDB path for a given flow type.
  const pathForType = (type) => type === 'warmup'
    ? `/World/${userId}/settings/warmupFlows`
    : `/World/${userId}/settings/leadFlows`;

  // Trigger options available for a given flow type.
  const triggerOptionsForType = (type) => type === 'warmup'
    ? TRIGGER_OPTIONS_WARMUP
    : TRIGGER_OPTIONS_RETARGETING;

  // Load flows from both paths so everything shows on one screen.
  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    const loaded = { warmup: false, retargeting: false };
    const markLoaded = (key) => {
      loaded[key] = true;
      if (loaded.warmup && loaded.retargeting) setLoading(false);
    };
    const readSnap = (snapshot) => {
      const arr = [];
      // NOTE: returning a truthy value from a RTDB forEach callback cancels the
      // iteration. `arr.push(...)` returns the new length (truthy), so the arrow
      // MUST use a block body that returns undefined — otherwise only the first
      // child is ever read.
      snapshot.forEach((child) => {
        arr.push({ id: child.key, ...child.val() });
      });
      return arr;
    };

    const warmRef = ref(database, `/World/${userId}/settings/warmupFlows`);
    const leadRef = ref(database, `/World/${userId}/settings/leadFlows`);

    const unsubWarm = onValue(
      warmRef,
      (snap) => {
        setWarmupFlows(readSnap(snap));
        markLoaded('warmup');
      },
      (err) => {
        console.error('Error loading warmup flows:', err);
        markLoaded('warmup');
      }
    );
    const unsubLead = onValue(
      leadRef,
      (snap) => {
        setRetargetingFlows(readSnap(snap));
        markLoaded('retargeting');
      },
      (err) => {
        console.error('Error loading lead flows:', err);
        markLoaded('retargeting');
      }
    );

    return () => { unsubWarm(); unsubLead(); };
  }, [userId]);

  // Close the touchpoints menu when clicking outside of it.
  useEffect(() => {
    if (!showTouchpointsMenu) return;
    const handleClickOutside = (e) => {
      if (touchpointsMenuRef.current && !touchpointsMenuRef.current.contains(e.target)) {
        setShowTouchpointsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTouchpointsMenu]);

  // Custom Touchpoints laden
  useEffect(() => {
    if (!userId) return;
    
    const customRef = ref(database, `/World/${userId}/settings/customTouchpoints`);
    const unsubscribe = onValue(customRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = [];
        snapshot.forEach((childSnapshot) => {
          data.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setCustomTouchpoints(data);
      } else {
        setCustomTouchpoints([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Email Topics laden (nur eigene customTopics)
  useEffect(() => {
    if (!userId) return;

    const customRef = ref(database, `/World/${userId}/settings/customTopics`);
    const unsub = onValue(customRef, (snapshot) => {
      const topics = [];
      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          topics.push({ id: child.key, ...child.val() });
        });
      }
      setEmailTopics(topics);
    });

    return () => unsub();
  }, [userId]);

  // Sender-Accounts aus Firestore laden
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(firestore, 'world_users', userId, 'emailAccounts'), orderBy('email'));
    const unsub = onSnapshot(q, (snapshot) => {
      const accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSenderEmails(accounts);
    }, () => setSenderEmails([]));
    return () => unsub();
  }, [userId]);

  // Alle verfügbaren Touchpoints (predefined + custom)
  const allTouchpoints = {
    ...TOUCHPOINT_OPTIONS,
    ...customTouchpoints.reduce((acc, tp) => {
      acc[`custom_${tp.id}`] = { label: tp.label, icon: tp.icon, points: tp.points, isCustom: true };
      return acc;
    }, {})
  };

  // Flow löschen
  const handleDeleteFlow = async (flow) => {
    if (!window.confirm("Are you sure you want to delete this flow?")) return;
    
    try {
      const flowRef = ref(database, `${pathForType(flow.flowType)}/${flow.id}`);
      await remove(flowRef);
    } catch (error) {
      console.error("Error deleting flow:", error);
    }
  };

  // Flow aktivieren/deaktivieren
  const toggleFlowActive = async (flow) => {
    try {
      const flowRef = ref(database, `${pathForType(flow.flowType)}/${flow.id}`);
      await update(flowRef, { isActive: !flow.isActive });
    } catch (error) {
      console.error("Error toggling flow:", error);
    }
  };

  // Flow duplizieren (innerhalb desselben Typs)
  const handleDuplicateFlow = async (flow) => {
    try {
      const type = flow.flowType || 'retargeting';
      // Copy everything except the id; name/type/timestamps are overridden below.
      const { id, ...rest } = flow;
      const flowsRef = ref(database, pathForType(type));
      const newRef = push(flowsRef);
      await set(newRef, {
        ...removeUndefined(rest),
        name: `${flow.name || 'Flow'} (Copy)`,
        flowType: type,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error("Error duplicating flow:", error);
    }
  };

  // Berechne totale Tage für einen Flow
  const calculateTotalDays = (steps) => {
    if (!steps) return 0;
    return steps.reduce((total, step) => total + (step.delayDays || 0), 0);
  };

  // Helper: Remove undefined values from object (Firebase doesn't accept undefined)
  const removeUndefined = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefined(item));
    }
    if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = removeUndefined(value);
        }
        return acc;
      }, {});
    }
    return obj;
  };

  // Helper: Sort nodes by following edges from trigger
  const getOrderedNodes = (nodes, edges) => {
    if (!nodes || !edges || nodes.length === 0) return nodes.filter(n => n.type !== 'trigger');
    
    const triggerNode = nodes.find(n => n.type === 'trigger');
    if (!triggerNode) return nodes.filter(n => n.type !== 'trigger');
    
    const orderedNodes = [];
    const visited = new Set();
    const queue = [triggerNode.id];
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      
      const currentNode = nodes.find(n => n.id === currentId);
      if (currentNode && currentNode.type !== 'trigger') {
        orderedNodes.push(currentNode);
      }
      
      const outgoingEdges = edges.filter(e => e.source === currentId);
      outgoingEdges.forEach(edge => {
        if (!visited.has(edge.target)) {
          queue.push(edge.target);
        }
      });
    }
    
    return orderedNodes;
  };

  // Visual Flow speichern
  const handleSaveVisualFlow = async (flowData) => {
    if (!visualFlowName.trim()) return;
    
    try {
      // Get nodes in correct order by following edges
      const orderedNodes = getOrderedNodes(flowData.nodes, flowData.edges);
      
      // Convert visual flow to step format for compatibility
      const steps = orderedNodes.map((node) => {
          // Base step data
          const step = {
            id: node.id,
            type: node.data?.actionType || node.type,
            delayDays: node.data?.delayDays || 0,
            note: node.data?.note || '',
            nodeType: node.type,
          };
          
          // Add type-specific data only if defined
          if (node.type === 'split') {
            step.splitA = node.data?.splitA ?? 50;
            step.splitB = node.data?.splitB ?? 50;
          }
          if (node.type === 'condition') {
            step.condition = node.data?.condition || 'email_opened';
            if (node.data?.customQuestion) {
              step.customQuestion = node.data.customQuestion;
            }
          }
          if (node.type === 'tag') {
            step.tags = node.data?.tags || [];
          }
          if (node.type === 'end') {
            step.endType = node.data?.endType || 'completed';
          }
          // Manual "Send Email" step can carry a template + sender override that
          // Normal/Bulk mode consume when a contact is on this step.
          if (node.type === 'action' && node.data?.actionType === 'email_sent') {
            step.templateId = node.data?.templateId || '';
            step.templateName = node.data?.templateName || '';
            step.senderMode = node.data?.senderMode || 'default';
            step.senderId = node.data?.senderId || '';
            step.senderName = node.data?.senderName || '';
            step.senderPool = Array.isArray(node.data?.senderPool) ? node.data.senderPool : [];
            // When enabled, the flow-automation scheduler generates + stores this
            // email in advance (backend prepare mode) instead of the user having
            // to generate it live. Only honored with a concrete templateId.
            step.prepareInBackend = node.data?.prepareInBackend === true && !!node.data?.templateId;
          }
          if (node.type === 'auto_send_email') {
            step.templateId = node.data?.templateId || '';
            step.templateName = node.data?.templateName || '';
            step.senderId = node.data?.senderId || 'random';
            step.senderName = node.data?.senderName || '';
          }
          if (node.type === 'auto_check_reply') {
            step.timeoutDays = node.data?.timeoutDays || 3;
          }
          if (node.type === 'data_capture') {
            step.captureFields = node.data?.captureFields || [];
          }
          
          return step;
        });

      // Clean visualData to remove undefined values
      const cleanVisualData = removeUndefined(flowData);

      const targetType = visualFlowType;
      const targetPath = pathForType(targetType);

      const flowPayload = {
        name: visualFlowName,
        description: '',
        trigger: visualFlowTrigger,
        triggerValue: visualFlowTriggerValue || '',
        steps: steps,
        isActive: editingFlow ? (editingFlow.isActive !== false) : true,
        isVisual: true,
        visualData: cleanVisualData,
        flowType: targetType, // Speichere den Flow-Typ
        updatedAt: Date.now()
      };

      if (editingFlow) {
        const oldType = editingFlow.flowType || targetType;
        const oldPath = pathForType(oldType);

        // Warn if contacts might be active in this flow (based on its current type)
        const contactsSnap = await import("firebase/firestore").then(m => {
          const { getDocs, where } = m;
          const flowField = oldType === 'warmup' ? 'warmupFlow.flowId' : 'activeFlow.flowId';
          return getDocs(query(collection(firestore, `World/${userId}/db`), where(flowField, '==', editingFlow.id)));
        }).catch(() => ({ size: 0 }));

        if (contactsSnap.size > 0) {
          const proceed = window.confirm(
            `${contactsSnap.size} contact${contactsSnap.size > 1 ? 's are' : ' is'} currently in this flow. ` +
            `Editing steps may affect their progress. Continue?`
          );
          if (!proceed) return;
        }

        if (oldPath === targetPath) {
          // Same type — plain update.
          await update(ref(database, `${targetPath}/${editingFlow.id}`), flowPayload);
        } else {
          // Type changed — move the flow to the other path, keeping its id.
          await set(ref(database, `${targetPath}/${editingFlow.id}`), {
            ...flowPayload,
            createdAt: editingFlow.createdAt || Date.now(),
          });
          await remove(ref(database, `${oldPath}/${editingFlow.id}`));
        }
      } else {
        // Create new
        const newFlowRef = push(ref(database, targetPath));
        await set(newFlowRef, {
          ...flowPayload,
          createdAt: Date.now()
        });
      }
      
      closeVisualBuilder();
    } catch (error) {
      console.error("Error saving visual flow:", error);
    }
  };

  const generateFlowName = () => {
    const existingNames = new Set(flows.map(f => f.name));
    let counter = 1;
    while (existingNames.has(`Flow ${counter}`)) {
      counter++;
    }
    return `Flow ${counter}`;
  };

  // Open visual builder for new flow
  const openNewFlow = () => {
    setVisualFlowData({ nodes: [], edges: [] });
    setVisualFlowName(generateFlowName());
    setVisualFlowType('warmup');
    setVisualFlowTrigger('manual');
    setVisualFlowTriggerValue('');
    setEditingFlow(null);
    setShowVisualBuilder(true);
  };

  // Change the flow type inside the builder; keep the trigger valid for the type.
  const handleVisualFlowTypeChange = (type) => {
    setVisualFlowType(type);
    const opts = triggerOptionsForType(type);
    if (!opts[visualFlowTrigger]) {
      setVisualFlowTrigger(type === 'warmup' ? 'manual' : 'lead_created');
      setVisualFlowTriggerValue('');
    }
  };

  // Open visual builder for editing
  const handleEditFlow = (flow) => {
    const flowTriggerOptions = triggerOptionsForType(flow.flowType);
    if (flow.visualData) {
      setVisualFlowData(flow.visualData);
    } else {
      // Convert old flow format to visual format
      const nodes = [
        {
          id: 'trigger-1',
          type: 'trigger',
          position: { x: 250, y: 50 },
          data: { triggerLabel: flowTriggerOptions[flow.trigger]?.label || 'Start' },
        },
        ...(flow.steps || []).map((step, index) => ({
          id: step.id || `action-${index}`,
          type: step.nodeType || 'action',
          position: { x: 250, y: 150 + (index * 120) },
          data: {
            actionType: step.type,
            delayDays: step.delayDays || 0,
            note: step.note || '',
            splitA: step.splitA,
            splitB: step.splitB,
            condition: step.condition,
            customQuestion: step.customQuestion,
            tags: step.tags,
            endType: step.endType,
            ...(step.nodeType === 'action' && step.type === 'email_sent' && {
              templateId: step.templateId || '',
              templateName: step.templateName || '',
              senderMode: step.senderMode || 'default',
              senderId: step.senderId || '',
              senderName: step.senderName || '',
              senderPool: Array.isArray(step.senderPool) ? step.senderPool : [],
            }),
            ...(step.nodeType === 'auto_send_email' && {
              templateId: step.templateId || '',
              templateName: step.templateName || '',
              senderId: step.senderId || 'random',
              senderName: step.senderName || '',
            }),
            ...(step.nodeType === 'auto_check_reply' && {
              timeoutDays: step.timeoutDays || 3,
            }),
          },
        }))
      ];
      
      // Create edges
      const edges = nodes.slice(0, -1).map((node, index) => ({
        id: `edge-${node.id}-${nodes[index + 1].id}`,
        source: node.id,
        target: nodes[index + 1].id,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#3b82f6', strokeWidth: 2 },
      }));
      
      setVisualFlowData({ nodes, edges });
    }
    
    setVisualFlowName(flow.name);
    setVisualFlowType(flow.flowType || 'retargeting');
    setVisualFlowTrigger(flow.trigger || (flow.flowType === 'warmup' ? 'manual' : 'lead_created'));
    setVisualFlowTriggerValue(flow.triggerValue || '');
    setEditingFlow(flow);
    setShowVisualBuilder(true);
  };

  // Close visual builder
  const closeVisualBuilder = () => {
    setShowVisualBuilder(false);
    setVisualFlowName('');
    setVisualFlowData({ nodes: [], edges: [] });
    setVisualFlowType('warmup');
    setVisualFlowTrigger('manual');
    setVisualFlowTriggerValue('');
    setEditingFlow(null);
  };

  // Custom Touchpoint speichern
  const saveCustomTouchpoint = async () => {
    if (!newCustomTouchpoint.label.trim()) return;
    
    try {
      const customRef = ref(database, `/World/${userId}/settings/customTouchpoints`);
      const newRef = push(customRef);
      await set(newRef, {
        ...newCustomTouchpoint,
        createdAt: Date.now()
      });
      setNewCustomTouchpoint({ label: '', icon: 'fa-star', points: 1 });
      setShowAddCustom(false);
    } catch (error) {
      console.error("Error saving custom touchpoint:", error);
    }
  };

  // Custom Touchpoint löschen
  const deleteCustomTouchpoint = async (id) => {
    try {
      const tpRef = ref(database, `/World/${userId}/settings/customTouchpoints/${id}`);
      await remove(tpRef);
    } catch (error) {
      console.error("Error deleting custom touchpoint:", error);
    }
  };

  if (loading) {
    return (
      <div className="leadSettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <div className="leadSettingsContainer">
      {/* Visual Flow Builder - full-width dedicated page */}
      {showVisualBuilder && (
        <FlowCreatorPage
          isEditing={!!editingFlow}
          flowName={visualFlowName}
          onFlowNameChange={setVisualFlowName}
          flowType={visualFlowType}
          onFlowTypeChange={handleVisualFlowTypeChange}
          trigger={visualFlowTrigger}
          onTriggerChange={setVisualFlowTrigger}
          triggerValue={visualFlowTriggerValue}
          onTriggerValueChange={setVisualFlowTriggerValue}
          triggerOptions={triggerOptionsForType(visualFlowType)}
          allTouchpoints={allTouchpoints}
          visualFlowData={visualFlowData}
          customTouchpoints={customTouchpoints}
          emailTopics={emailTopics}
          senderEmails={senderEmails}
          onSave={handleSaveVisualFlow}
          onClose={closeVisualBuilder}
        />
      )}

      {/* Top bar with overflow menu for less-used settings */}
      <div className="flowsTopBar">
        <div className="flowsTopBarTitle">
          <h3>Flows</h3>
          <span>Warmup &amp; retargeting sequences</span>
        </div>
        <div className="flowsMenuWrap" ref={touchpointsMenuRef}>
          <button
            className={`flowsMenuBtn ${showTouchpointsMenu ? 'active' : ''}`}
            onClick={() => setShowTouchpointsMenu((open) => !open)}
            title="More options"
          >
            <i className="fa fa-ellipsis-v"></i>
          </button>

          {showTouchpointsMenu && (
            <div className="flowsMenuPopover">
              <div className="customTouchpointsHeader">
                <h3>
                  <i className="fa fa-puzzle-piece"></i>
                  Custom Touchpoints
                </h3>
                <button
                  className="addCustomBtn"
                  onClick={() => setShowAddCustom(!showAddCustom)}
                >
                  <i className={`fa ${showAddCustom ? 'fa-times' : 'fa-plus'}`}></i>
                </button>
              </div>

              {showAddCustom && (
                <div className="addCustomForm">
                  <input
                    type="text"
                    placeholder="Touchpoint name..."
                    value={newCustomTouchpoint.label}
                    onChange={(e) => setNewCustomTouchpoint(prev => ({ ...prev, label: e.target.value }))}
                    className="customTpNameInput"
                  />
                  <div className="customTpPointsSlider">
                    <span className="sliderLabel">Points:</span>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      step="1"
                      value={newCustomTouchpoint.points}
                      onChange={(e) => setNewCustomTouchpoint(prev => ({ ...prev, points: parseInt(e.target.value) }))}
                    />
                    <span className="sliderValue">{newCustomTouchpoint.points}</span>
                  </div>
                  <button onClick={saveCustomTouchpoint} disabled={!newCustomTouchpoint.label.trim()}>
                    <i className="fa fa-check"></i>
                  </button>
                </div>
              )}

              <div className="customTouchpointsList">
                {customTouchpoints.length === 0 ? (
                  <p className="customTouchpointsEmpty">No custom touchpoints yet</p>
                ) : (
                  customTouchpoints.map(tp => (
                    <div key={tp.id} className="customTouchpointItem">
                      <i className={`fa ${tp.icon}`}></i>
                      <span className="customTpLabel">{tp.label}</span>
                      <span className="customTpPoints">{tp.points} pts</span>
                      <button onClick={() => deleteCustomTouchpoint(tp.id)}>
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Existing Flows List */}
      <div className="flowsList">
        {flows.length === 0 ? (
          <div className="flowsEmpty">
            <div className="flowsEmpty-content">
              <div className="flowsEmptyIcon">
                <i className="fa fa-project-diagram"></i>
              </div>
              <h4>Create Your First Flow</h4>
              <p>
                Build value-first, multi-touch outreach sequences. Pick whether a flow is a
                Warmup (for cold contacts) or a Retargeting flow (for qualified leads) right
                inside the editor.
              </p>

              <div className="flowBenefits">
                <div className="benefit">
                  <div className="benefitIcon warmup">
                    <i className="fa fa-fire"></i>
                  </div>
                  <div className="benefitText">
                    <strong>Warmup Flows</strong>
                    <span>Warm up cold contacts across email, LinkedIn and calls</span>
                  </div>
                </div>
                <div className="benefit">
                  <div className="benefitIcon">
                    <i className="fa fa-redo"></i>
                  </div>
                  <div className="benefitText">
                    <strong>Retargeting Flows</strong>
                    <span>Re-engage and nurture qualified leads with timed follow-ups</span>
                  </div>
                </div>
                <div className="benefit">
                  <div className="benefitIcon">
                    <i className="fa fa-code-branch"></i>
                  </div>
                  <div className="benefitText">
                    <strong>Smart Branching</strong>
                    <span>Branch on interest, replies and conditions to close deals</span>
                  </div>
                </div>
              </div>

              <button className="flowsEmptyBtn" onClick={openNewFlow}>
                <i className="fa fa-plus"></i>
                Create First Flow
              </button>
            </div>
          </div>
        ) : (
          <>
            {flows.map(flow => (
              <div key={`${flow.flowType}-${flow.id}`} className={`flowCard ${!flow.isActive ? 'inactive' : ''}`}>
                <div className="flowCardHeader">
                  <div className="flowCardInfo">
                    <h4>
                      <span className="flowVisualIcon">
                        <img src="/img/star.svg" alt="" />
                      </span>
                      {flow.name}
                      <span className={`flowTypeBadge ${flow.flowType}`}>
                        <i className={`fa ${flow.flowType === 'warmup' ? 'fa-fire' : 'fa-redo'}`}></i>
                        {flow.flowType === 'warmup' ? 'Warmup' : 'Retargeting'}
                      </span>
                    </h4>
                    {flow.description && <p>{flow.description}</p>}
                  </div>
                  <div className="flowCardActions">
                    <button 
                      className={`flowToggleBtn ${flow.isActive ? 'active' : ''}`}
                      onClick={() => toggleFlowActive(flow)}
                      title={flow.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <i className={`fa ${flow.isActive ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                    </button>
                    <button 
                      className="flowDuplicateBtn"
                      onClick={() => handleDuplicateFlow(flow)}
                      title="Duplicate flow"
                    >
                      <i className="fa fa-copy"></i>
                    </button>
                    <button 
                      className="flowEditBtn"
                      onClick={() => handleEditFlow(flow)}
                      title="Edit flow"
                    >
                      <i className="fa fa-pen"></i>
                    </button>
                    <button 
                      className="flowDeleteBtn"
                      onClick={() => handleDeleteFlow(flow)}
                      title="Delete flow"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
                
                <div className="flowCardMeta">
                  <span className="flowTrigger">
                    <i className={`fa ${triggerOptionsForType(flow.flowType)[flow.trigger]?.icon || 'fa-play'}`}></i>
                    {triggerOptionsForType(flow.flowType)[flow.trigger]?.label || flow.trigger}
                    {flow.triggerValue && ` (${flow.triggerValue})`}
                  </span>
                  <span className="flowSteps">
                    <i className="fa fa-layer-group"></i>
                    {flow.steps?.length || 0} nodes
                  </span>
                  <span className="flowDuration">
                    <i className="fa fa-clock"></i>
                    {calculateTotalDays(flow.steps)} days
                  </span>
                </div>
                
                {/* Visual Flow Mini Preview */}
                <div className="flowPreview">
                  {(flow.steps || []).slice(0, 5).map((step, idx) => {
                    const nodeType = step.nodeType || 'action';
                    const tp = allTouchpoints[step.type];
                    
                    const getNodeIcon = () => {
                      if (nodeType === 'split') return 'fa-code-branch';
                      if (nodeType === 'condition') return 'fa-question';
                      if (nodeType === 'delay') return 'fa-hourglass-half';
                      if (nodeType === 'end') return 'fa-flag-checkered';
                      return tp?.icon || 'fa-circle';
                    };
                    
                    const getNodeClass = () => {
                      if (nodeType === 'split') return 'splitPreview';
                      if (nodeType === 'condition') return 'conditionPreview';
                      if (nodeType === 'delay') return 'delayPreview';
                      if (nodeType === 'end') return 'endPreview';
                      return '';
                    };
                    
                    return (
                      <div key={idx} className="flowPreviewStep">
                        <div className={`flowPreviewIcon ${getNodeClass()}`}>
                          <i className={`${tp?.isBrand ? 'fab' : 'fa'} ${getNodeIcon()}`}></i>
                        </div>
                        {idx < Math.min((flow.steps || []).length - 1, 4) && (
                          <div className="flowPreviewLine">
                            {step.delayDays > 0 && <span>{step.delayDays}d</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(flow.steps || []).length > 5 && (
                    <div className="flowPreviewMore">+{flow.steps.length - 5}</div>
                  )}
                </div>
              </div>
            ))}
            <button className="flowAddCard" onClick={openNewFlow}>
              <div className="flowAddCardInner">
                <div className="flowAddCardIcon">
                  <i className="fa fa-plus"></i>
                </div>
                <span>Add New Flow</span>
              </div>
            </button>
          </>
        )}
      </div>

    </div>
  );
};

export default LeadSettings;
